import { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { createRoot, type Root } from 'react-dom/client';
import mapboxgl from 'mapbox-gl';
import Supercluster from 'supercluster';
import { EviMapDot } from '@evi/ui';
import styles from './PwaMapMarkers.module.css';
import type { PublicLocation, PublicLocationDetailCharger } from '@/lib/api';
import {
  resolveChargerAccessibility,
  type ChargerAccessibility,
} from '@/lib/mapChargerAccessibility';
import { useMapPrefsStore } from '@/store/mapPrefs';

// Re-declared here for the component's internal use. MapPage owns the
// canonical type (LocationWithChargers) but doesn't export it; rather
// than couple the marker layer to MapPage's private union we keep a
// structural alias of the same fields. Identical at the type level —
// LocationWithChargers extends PublicLocation with a chargers array,
// which is exactly this.
interface LocationWithChargers extends PublicLocation {
  chargers: PublicLocationDetailCharger[];
}

// ── Status → dot-presentation helpers ─────────────────────────────────
// Lifted from MapPage's existing logic (dotPresentation / getDominantStatus).
// Recomputed here so the marker layer can render without prop-drilling
// the full status table. Behaviour identical to the Google-era render.

type DominantStatus =
  | 'all-available'
  | 'partial-active'
  | 'fully-occupied'
  | 'maintenance'
  | 'offline';

const STATUS_LABEL: Record<DominantStatus, string> = {
  'all-available': 'Available',
  'partial-active': 'Partially active',
  'fully-occupied': 'Fully occupied',
  'maintenance': 'Maintenance',
  'offline': 'Offline',
};

// Connector status sets — keep in lockstep with MapPage's
// UNUSABLE_CONNECTOR_STATUSES / ACTIVE_CONNECTOR_STATUSES (the marker
// layer doesn't import them to avoid a cross-file cycle; the small
// duplication is acceptable for two values).
const UNUSABLE_CONNECTOR_STATUSES = new Set(['Faulted', 'Unavailable']);
const ACTIVE_CONNECTOR_STATUSES = new Set(['Preparing', 'Charging', 'Finishing', 'SuspendedEVSE', 'SuspendedEV']);

function getDominantStatus(loc: LocationWithChargers): DominantStatus {
  const now = new Date();
  const inMaint = loc.chargers.some((c) =>
    (c.maintenance_windows ?? []).some((w) => new Date(w.start) <= now && now <= new Date(w.end)),
  );
  if (inMaint) return 'maintenance';

  const onlineChargers = loc.chargers.filter((c) => c.is_online);
  if (onlineChargers.length === 0) return 'offline';

  const usable = onlineChargers
    .flatMap((c) => c.connectors)
    .filter((conn) => !UNUSABLE_CONNECTOR_STATUSES.has(conn.status));
  if (usable.length === 0) return 'offline';

  const availableCount = usable.filter((conn) => conn.status === 'Available').length;
  const activeCount = usable.filter((conn) => ACTIVE_CONNECTOR_STATUSES.has(conn.status)).length;

  if (availableCount > 0 && activeCount === 0) return 'all-available';
  if (availableCount > 0 && activeCount > 0) return 'partial-active';
  if (availableCount === 0 && activeCount > 0) return 'fully-occupied';
  return 'offline';
}

function dotPresentation(
  loc: LocationWithChargers,
  status: DominantStatus,
): { tone: 'brand' | 'muted' | 'warn' | 'guest' | 'occupied'; partialActive: boolean } {
  if (status === 'maintenance') return { tone: 'warn', partialActive: false };
  if (status === 'offline') return { tone: 'muted', partialActive: false };
  if (status === 'fully-occupied') return { tone: 'occupied', partialActive: false };
  const allGuest =
    loc.chargers.length > 0
    && loc.chargers.every((c) => c.access_mode === 'guest');
  const tone: 'brand' | 'guest' = allGuest ? 'guest' : 'brand';
  return { tone, partialActive: status === 'partial-active' };
}

// Five-bucket connector-status tally for a cluster ring. The buckets +
// OCPP-status grouping mirror the station-detail status grid exactly
// (StationDetailPage.tsx ~line 470) so the cluster segments read as the
// same status code drivers see on the detail page. Each connector lands in
// one bucket; an offline charger's connectors all count Unavailable
// (is_online wins), and any unknown status falls through to Unavailable so
// nothing silently vanishes. conn.status types as plain `string` here, so
// the switch is a value comparison.
interface StatusTally {
  available: number;
  charging: number;
  preparing: number;
  pluggedIn: number;
  unavailable: number;
}

function statusTallyForLocation(loc: LocationWithChargers): StatusTally {
  const tally: StatusTally = { available: 0, charging: 0, preparing: 0, pluggedIn: 0, unavailable: 0 };
  for (const c of loc.chargers) {
    for (const conn of c.connectors ?? []) {
      if (!c.is_online) { tally.unavailable += 1; continue; }
      switch (conn.status) {
        case 'Available': tally.available += 1; break;
        case 'Preparing': tally.preparing += 1; break;
        case 'Charging':
        case 'SuspendedEVSE':
        case 'Occupied': tally.charging += 1; break;
        case 'SuspendedEV':
        case 'Finishing': tally.pluggedIn += 1; break;
        case 'Unavailable':
        case 'Faulted':
        case 'Reserved': tally.unavailable += 1; break;
        default: tally.unavailable += 1; break;
      }
    }
  }
  return tally;
}

// Defensive coordinate normalization. Lifted from MapPage (same DB
// bug warranted normalisation there) so the marker layer doesn't
// rely on MapPage having pre-cleaned the data.
// A location dropped for bad coordinates is warned about once per load,
// not on every list refresh.
const warnedBadCoordinates = new Set<string>();
function warnBadCoordinates(id: string, raw: unknown): void {
  if (warnedBadCoordinates.has(id)) return;
  warnedBadCoordinates.add(id);
  console.warn(`[PwaMapMarkers] Location ${id} has no usable coordinates; it is left off the map.`, raw);
}

function normalizeLatLng(
  raw: { lat?: number; lng?: number } | undefined,
  id: string,
): { lat: number; lng: number } | null {
  if (!raw || typeof raw.lat !== 'number' || typeof raw.lng !== 'number'
    || !isFinite(raw.lat) || !isFinite(raw.lng)) {
    warnBadCoordinates(id, raw);
    return null;
  }
  let { lat, lng } = raw;
  let lngGuard = 0;
  while (Math.abs(lng) > 180 && lngGuard < 18) {
    lng /= 10;
    lngGuard += 1;
  }
  let latGuard = 0;
  while (Math.abs(lat) > 90 && latGuard < 18) {
    lat /= 10;
    latGuard += 1;
  }
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    warnBadCoordinates(id, raw);
    return null;
  }
  return { lat, lng };
}

const CLUSTER_RADIUS_PX = 60;
const CLUSTER_MAX_ZOOM = 16;

// Pulse-ring color per dot tone — kept in lockstep with EviMapDot's
// .core background colors so the host ripple reads as the same status as
// the dot it sits behind. The muted (offline) entry is deliberately a
// visible grey, not transparent: grey/offline locations get a findable
// pulse too, which EviMapDot's static muted core does not provide.
const PULSE_COLORS: Record<'brand' | 'muted' | 'warn' | 'guest' | 'occupied', string> = {
  brand: '#21c163', // green — available
  muted: '#aeb6c2', // grey — offline (brighter cool grey; .markerPulseMuted darkens it on light basemaps)
  warn: '#f39c12', // amber — maintenance
  guest: '#f59e0b', // amber — guest-eligible
  occupied: '#3498db', // blue — fully occupied
};

// Movement-tolerant pointer tap for marker hosts. Fires onTap on pointerup
// only when the pointer stayed within the slop threshold of pointerdown, so a
// drag (pan) is not mistaken for a tap.
//
// The two touch-hardening lines are what make this work on a phone — they are
// why a cluster zoomed on desktop (mouse) but did nothing on touch:
//   - touch-action:none stops the browser from claiming the touch as a
//     scroll/pan gesture, which would fire pointercancel instead of pointerup
//     and silently drop the tap.
//   - setPointerCapture binds the gesture to this element so pointerup still
//     lands here even when mapbox-gl pans the lat/lng-anchored marker out from
//     under the finger mid-tap (a mouse cursor never drifts, so desktop never
//     hit this). NB the pointer-event stopPropagation does NOT stop mapbox's
//     drag-pan — mapbox listens on the separate touch-event stream — so it is
//     capture, not propagation, that keeps the tap.
const TAP_SLOP_PX = 10;
function attachTap(el: HTMLElement, onTap: () => void): void {
  el.style.touchAction = 'none';
  let startX = 0;
  let startY = 0;
  el.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    el.setPointerCapture(e.pointerId);
    startX = e.clientX;
    startY = e.clientY;
  });
  el.addEventListener('pointerup', (e) => {
    e.stopPropagation();
    if (Math.hypot(e.clientX - startX, e.clientY - startY) <= TAP_SLOP_PX) onTap();
  });
}

// ──────────────────────────────────────────────────────────────────────
// PwaMapMarkers
// ──────────────────────────────────────────────────────────────────────

interface PwaMapMarkersProps {
  /** Live mapbox-gl instance from PwaMapContainer.onMapReady. */
  map: mapboxgl.Map;
  /** Locations to render. Already coord-validated upstream; the
   *  marker layer re-normalises defensively. */
  locations: LocationWithChargers[];
  /** Currently selected location id. Drives the active-state pulse
   *  on the matching dot. */
  selectedId: string | null;
  /** Fires when the driver taps a single-location marker (not a
   *  cluster). MapPage uses this to open the BottomSheet + Popup. */
  onSelect: (loc: LocationWithChargers) => void;
  /** Fleet Mode Phase 9 — driver state for the per-marker visual
   *  treatment. When omitted (legacy callers / non-driver views),
   *  the marker layer renders unmuted regardless of fleet
   *  attribution. When provided, locations whose chargers fall into
   *  the `mode_mismatch` or `inaccessible` accessibility buckets
   *  render with opacity / grayscale overlays. */
  userMode?: 'fleet' | 'public';
  userFleetId?: string | null;
}

/**
 * Aggregate a location's accessibility for marker tone. A location
 * with mixed accessibility (some accessible, some not) collapses to
 * the BEST level — drivers should see the location as accessible if
 * ANY charger there is usable. Same predicate as the Phase 2 gate
 * applied per charger, then unioned at the location level.
 *
 *   any accessible          → 'accessible'
 *   else any mode_mismatch  → 'mode_mismatch'
 *   else (all inaccessible) → 'inaccessible'
 */
function locationAccessibility(
  loc: LocationWithChargers,
  userMode: 'fleet' | 'public',
  userFleetId: string | null,
): ChargerAccessibility {
  const chargers = loc.chargers ?? [];
  if (chargers.length === 0) return 'accessible';
  let sawMismatch = false;
  for (const c of chargers) {
    const a = resolveChargerAccessibility(
      {
        fleet_id: c.fleet_id ?? null,
        fleet_access_mode: c.fleet_access_mode,
        // Fleet Mode map-visibility extension — surfacing
        // access_mode lets the classifier short-circuit to
        // 'inaccessible' on the new 'guest_closed' value
        // (chargers visible only because the caller requested
        // public-mode visibility, where the operator hasn't
        // opted in to guest access).
        access_mode: c.access_mode,
      },
      { fleet_id: userFleetId, current_mode: userMode },
    );
    if (a === 'accessible') return 'accessible';
    if (a === 'mode_mismatch') sawMismatch = true;
  }
  return sawMismatch ? 'mode_mismatch' : 'inaccessible';
}

/**
 * Imperative mapbox-gl marker layer. Replaces the Phase 1-disabled
 * <AdvancedMarker> + <EviMapDot> JSX with vanilla DOM + React roots
 * mounted into marker host elements.
 *
 * Lifecycle: on every (zoom, bounds) change supercluster recomputes
 * the visible bucket set; we tear down all existing markers + their
 * React roots and recreate. Mirrors the admin LocationMarker pattern
 * but at the layer level (admin recomputes its marker set the same
 * way via re-renders driven by camera state).
 *
 * createRoot vs createPortal: this component uses `createRoot` per
 * marker (one React tree per marker host div). Portals would require
 * a stable parent container in React's tree, which doesn't fit the
 * imperative add/remove pattern mapbox-gl uses. createRoot lets each
 * marker live in its own detached tree with proper unmount semantics.
 */
export function PwaMapMarkers({
  map, locations, selectedId, onSelect,
  userMode, userFleetId,
}: PwaMapMarkersProps) {
  // Device-local cosmetic preference: when off (default), the cluster beacons
  // skip the status-segmented ring and render as plain glowing dots. Reading it
  // from the store here re-renders the markers live when the Settings toggle
  // flips (the value is in the marker effect's deps below).
  const showClusterStatusRing = useMapPrefsStore((s) => s.showClusterStatusRing);

  // Camera state tracked locally so supercluster recomputes whenever
  // the map settles to a new zoom or pan. moveend covers both.
  const [camera, setCamera] = useState<{ zoom: number; bbox: [number, number, number, number] }>(() => {
    const b = map.getBounds();
    return {
      zoom: Math.round(map.getZoom()),
      bbox: b
        ? [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]
        : [-180, -85, 180, 85],
    };
  });

  useEffect(() => {
    const update = () => {
      const b = map.getBounds();
      setCamera({
        zoom: Math.round(map.getZoom()),
        bbox: b
          ? [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]
          : [-180, -85, 180, 85],
      });
    };
    map.on('moveend', update);
    map.on('zoomend', update);
    return () => {
      map.off('moveend', update);
      map.off('zoomend', update);
    };
  }, [map]);

  // Supercluster index. Rebuilds when locations change; otherwise
  // re-used across camera ticks (cheap getClusters call against the
  // current bbox + zoom).
  const cluster = useMemo(() => {
    const sc = new Supercluster<
      { loc: LocationWithChargers; chargerCount: number; status: DominantStatus; tally: StatusTally },
      { chargerSum: number; tally: StatusTally }
    >({
      radius: CLUSTER_RADIUS_PX,
      maxZoom: CLUSTER_MAX_ZOOM,
      // Seed a cluster accumulator from one leaf: its charger count plus a
      // COPY of its status tally (copied so reduce mutates the cluster's own
      // object, never a leaf's shared one).
      map: (props) => ({ chargerSum: props.chargerCount, tally: { ...props.tally } }),
      reduce: (accumulated, props) => {
        accumulated.chargerSum += props.chargerSum;
        accumulated.tally.available += props.tally.available;
        accumulated.tally.charging += props.tally.charging;
        accumulated.tally.preparing += props.tally.preparing;
        accumulated.tally.pluggedIn += props.tally.pluggedIn;
        accumulated.tally.unavailable += props.tally.unavailable;
      },
    });
    sc.load(
      locations
        .map((l) => {
          const normalized = normalizeLatLng(l.address.coordinates, l._id);
          return normalized ? { loc: l, normalized } : null;
        })
        .filter((x): x is { loc: LocationWithChargers; normalized: { lat: number; lng: number } } => x !== null)
        .map(({ loc, normalized }) => ({
          type: 'Feature' as const,
          properties: {
            loc,
            chargerCount: loc.chargers.length,
            status: getDominantStatus(loc),
            tally: statusTallyForLocation(loc),
          },
          geometry: {
            type: 'Point' as const,
            coordinates: [normalized.lng, normalized.lat],
          },
        })),
    );
    return sc;
  }, [locations]);

  const clusters = useMemo(
    () => cluster.getClusters(camera.bbox, camera.zoom),
    [cluster, camera],
  );

  // Active marker tracker. Each entry holds the mapbox marker + its
  // React root so cleanup can unmount the React tree before tearing
  // down the marker. Recreated on every clusters change.
  const markersRef = useRef<Array<{ marker: mapboxgl.Marker; root: Root }>>([]);

  // Stable callback refs so the marker click handlers don't capture
  // stale closures when selectedId / onSelect change between renders.
  const onSelectRef = useRef(onSelect);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

  useEffect(() => {
    // Teardown happens at the START of every effect run rather than
    // in the returned cleanup. Reason: React's useEffect cleanup
    // closure captures `prior` at the body's first line, but the
    // cleanup itself doesn't fire until the NEXT effect run's start
    // — meaning by the time it runs, the NEW markers have already
    // been added on top of the prior ones. Net effect: duplicate
    // markers accumulating on every selectedId/clusters change.
    // Tearing down here instead keeps the map at exactly the current
    // marker set after each run; the empty-deps effect below handles
    // unmount-on-page-leave.
    for (const { marker, root } of markersRef.current) {
      marker.remove();
      queueMicrotask(() => root.unmount());
    }
    markersRef.current = [];

    const next: Array<{ marker: mapboxgl.Marker; root: Root }> = [];
    for (const c of clusters) {
      const [lng, lat] = c.geometry.coordinates as [number, number];
      const isCluster = (c.properties as { cluster?: boolean }).cluster === true;

      const el = document.createElement('div');
      // 48px hit area centered on the lat/lng point (mapboxgl.Marker's
      // default 'center' anchor), with the 36px EviMapDot centered inside so
      // the visual sits in the middle of transparent, tappable padding. The
      // dot is shared with the admin map, so the larger touch target lives on
      // this web host, not in EviMapDot. attachTap (below) owns the gesture.
      el.style.width = '48px';
      el.style.height = '48px';
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
      el.style.cursor = 'pointer';
      // EviMapDot renders into its own mount (not the host directly) so a
      // pulse ring can sit as an imperative sibling behind it without React
      // reconciling it away. The mount is raised above the pulse's z-index
      // so the dot — and its selected-state highlight — stay on top.
      const dotMount = document.createElement('div');
      dotMount.style.position = 'relative';
      dotMount.style.zIndex = '1';
      dotMount.style.display = 'flex';
      el.appendChild(dotMount);
      const root = createRoot(dotMount);

      if (isCluster) {
        const clusterProps = c.properties as { cluster_id: number; chargerSum: number; tally: StatusTally };
        // flushSync forces React to commit BEFORE mapbox-gl reads
        // the DOM in setLngLat / measure-on-add. Without it, the
        // marker is added with an empty host and mapbox-gl positions
        // around a zero-size element, which produces visual glitches
        // and (for popups, see MapboxLocationPopup) an entirely
        // invisible bubble. Same fix applies here defensively even
        // though markers themselves position by lat/lng coordinates
        // rather than measure.
        flushSync(() => {
          // Driver cluster path only: pass the five-bucket tally so EviMapDot
          // draws the status-segmented ring. Single dots + the admin map never
          // pass statusCounts, so the ring renders here only.
          // Gate the ring on the device preference (default off -> plain dot).
          // The glow + pulse are EviMapDot's own and render either way; only
          // the statusCounts prop (which draws the ring) is withheld when off.
          root.render(
            <EviMapDot
              count={clusterProps.chargerSum}
              statusCounts={showClusterStatusRing ? clusterProps.tally : undefined}
            />,
          );
        });
        attachTap(el, () => {
          // Zoom IN to break the cluster apart. Prefer supercluster's
          // expansion zoom (the zoom at which THIS cluster splits); fall
          // back to a fixed +2.5 step when it isn't computable. Either way
          // guarantee forward progress (never below current + a step) and
          // clamp to the map's maxZoom (18 — not the cluster index's 16) so
          // one tap visibly separates the cluster and repeated taps continue
          // until individual pins show.
          const currentZoom = map.getZoom();
          let targetZoom: number;
          try {
            targetZoom = cluster.getClusterExpansionZoom(clusterProps.cluster_id);
          } catch {
            targetZoom = currentZoom + 2.5;
          }
          targetZoom = Math.min(18, Math.max(targetZoom, currentZoom + 2.5));
          // Defer the camera move out of the pointerup handler. An easeTo
          // called synchronously inside the tap's touch/pointer event is
          // neutralized by mapbox-gl's own same-tick gesture handling, so
          // the cluster never zooms. The single-tap selection camera
          // (MapboxSelectionCamera, a React effect) and the locate-me FAB
          // (an async geolocation callback) both work precisely because
          // their easeTo runs later; one rAF lands this after the tap's
          // event sequence fully resolves.
          requestAnimationFrame(() => {
            map.easeTo({ center: [lng, lat], zoom: targetZoom, duration: 350 });
          });
        });
      } else {
        const { loc, chargerCount, status } = c.properties as {
          loc: LocationWithChargers;
          chargerCount: number;
          status: DominantStatus;
        };
        const presentation = dotPresentation(loc, status);

        // Phase 9 — fleet accessibility overlay. Applied to the host
        // element (vs. the EviMapDot itself) so the marker's
        // pulse/glow/status semantics stay intact for the
        // accessible path; muting kicks in only for non-driver views
        // (no userMode passed) and accessible markers stay at full
        // strength.
        //   accessible     → no change
        //   mode_mismatch  → 0.55 opacity (still tappable, visually
        //                    deprioritized, retains color hint so
        //                    the driver sees "this exists for me if
        //                    I switch mode")
        //   inaccessible   → 0.32 opacity + grayscale (still
        //                    tappable for "not available to your
        //                    account" copy in the popup; clearly
        //                    not a usable target)
        const accessibility: ChargerAccessibility = userMode
          ? locationAccessibility(loc, userMode, userFleetId ?? null)
          : 'accessible';
        if (accessibility === 'mode_mismatch') {
          el.style.opacity = '0.55';
        } else if (accessibility === 'inaccessible') {
          el.style.opacity = '0.32';
          el.style.filter = 'grayscale(0.85)';
        }

        // Pulsing beacon — a soft outward ripple behind the dot, colored
        // by the dot's tone, so every location stays findable (grey/offline
        // dots included, which EviMapDot's static muted core doesn't pulse).
        // Host-only by design: EviMapDot is shared with the admin map. Sits
        // behind dotMount (lower z-index), so the dot and its selected-state
        // highlight stay on top. Clusters skip it — the count is the priority
        // there and overlapping ripples would read as noise.
        const pulse = document.createElement('span');
        // ?? '' satisfies noUncheckedIndexedAccess (CSS-module values type
        // as string | undefined) on the DOM className setter, which wants a
        // plain string; the class is always present, so the fallback is a
        // graceful no-op rather than expected behaviour.
        // Grey/offline dots get a stronger pulse via the muted modifier
        // (higher peak opacity + a darker grey on light basemaps), since a
        // desaturated grey otherwise reads weaker than the saturated tones.
        const pulseBase = styles.markerPulse ?? '';
        pulse.className = presentation.tone === 'muted'
          ? `${pulseBase} ${styles.markerPulseMuted ?? ''}`
          : pulseBase;
        pulse.style.setProperty('--marker-pulse-color', PULSE_COLORS[presentation.tone]);
        el.insertBefore(pulse, dotMount);

        flushSync(() => {
          root.render(
            <EviMapDot
              count={chargerCount > 1 ? chargerCount : undefined}
              tone={presentation.tone}
              partialActive={presentation.partialActive}
              active={selectedId === loc._id}
              ariaLabel={`${loc.name} — ${chargerCount} charger${chargerCount === 1 ? '' : 's'}, ${STATUS_LABEL[status]}${
                accessibility === 'mode_mismatch' ? ' (switch mode to access)'
                  : accessibility === 'inaccessible' ? ' (not available to your account)'
                    : ''
              }`}
            />,
          );
        });
        attachTap(el, () => onSelectRef.current(loc));
      }

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([lng, lat])
        .addTo(map);
      next.push({ marker, root });
    }
    markersRef.current = next;
    // Phase 9 — re-run the marker render when the driver flips mode
    // or their fleet attribution changes so the per-location
    // accessibility overlay refreshes without a route change.
  }, [map, clusters, cluster, selectedId, userMode, userFleetId, showClusterStatusRing]);

  // Final unmount when the component itself unmounts (parent flips
  // map to null, or the page unmounts). The dep-array effect above
  // only cleans up the PRIOR render's markers; this effect handles
  // the live set on full teardown.
  useEffect(() => {
    return () => {
      for (const { marker, root } of markersRef.current) {
        marker.remove();
        queueMicrotask(() => root.unmount());
      }
      markersRef.current = [];
    };
  }, []);

  return null;
}
